import React, { useEffect, useCallback, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  Panel,
  useReactFlow,
  ReactFlowProvider,
  Connection,
} from "reactflow";
import "reactflow/dist/style.css";
import { Bot } from "lucide-react";
import { useUIStore } from "../store";
import { usePipelineBuilderStore } from "../store/pipelineBuilderStore";
import {
  SystemInputNode,
  ProcessorPalette,
  ProcessorNode,
  OrchestratorNode,
  InputProcessorNode,
  PropertiesPanel,
  ExecutionFlowEdge,
  RemoveExecutionConfirmModal,
  CustomConnectionLine,
  PipelineToolbar,
  SubAgentCreationDialog,
  InputNameDialog,
} from "../components/PipelineBuilder";
// import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning'; // Temporarily disabled for testing
import { SYSTEM_INPUT_NODE } from "../constants/systemNodes";
import { apiService } from "../services/api";
import { useNotificationStore } from "../store";
import {
  findDependentDataEdges,
  calculateExecutionOrder,
  buildPipelineSteps,
} from "../utils/executionFlow";
import { formatApiError } from "../utils/errorFormatter";
import { AssistantSidebar } from "../features/pipeline-assistant";

// Hide React Flow attribution
const hideAttributionStyle = `
  .react-flow__attribution {
    display: none !important;
  }
`;

// Inject style
if (typeof document !== "undefined") {
  const styleId = "react-flow-attribution-hide";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = hideAttributionStyle;
    document.head.appendChild(style);
  }
}

const nodeTypes = {
  systemInput: SystemInputNode,
  processor: ProcessorNode,
  orchestrator: OrchestratorNode,
  inputProcessor: InputProcessorNode,
};

const edgeTypes = {
  execution: ExecutionFlowEdge,
};

// Inner component that needs ReactFlow context for drop handling
const PipelineBuilderInner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useUIStore((state) => state.theme);
  const addNotification = useNotificationStore(
    (state) => state.addNotification,
  );

  const {
    nodes,
    edges,
    isLoading,
    // pipelineId is from store but not used directly in this component
    pipelineName,
    pipelineDescription,
    pipelineVersion,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    loadPipeline,
    reset,
    saveOriginalState,
    setLoading,
    selectNode,
    markClean,
    setValidationResults,
    addSubAgent,
    pendingInputConnection,
    setPendingInputConnection,
    addCustomInput,
  } = usePipelineBuilderStore();

  // Initialize canvas
  useEffect(() => {
    const initializePipeline = async () => {
      if (id) {
        // Edit mode - load pipeline from API
        setLoading(true);
        try {
          const pipeline = await apiService.getPipeline(parseInt(id, 10));
          await loadPipeline(pipeline);

          // Check for loading warnings and display them
          const loadingWarnings =
            usePipelineBuilderStore.getState().loadingWarnings;
          if (loadingWarnings.length > 0) {
            loadingWarnings.forEach((warning) => {
              addNotification(warning, "warning");
            });
          }

          // Update edge colors for current theme after loading
          usePipelineBuilderStore.getState().updateEdgeColorsForTheme(theme);

          // Auto-validate after loading
          // Wait a tick to ensure nodes/edges are properly set
          setTimeout(async () => {
            try {
              const currentNodes = usePipelineBuilderStore.getState().nodes;
              const currentEdges = usePipelineBuilderStore.getState().edges;
              const steps = buildPipelineSteps(currentNodes, currentEdges);

              const validationResults = await apiService.validatePipeline({
                steps,
                name: pipeline.name,
              });

              setValidationResults(validationResults);
            } catch (error) {
              console.error("Auto-validation failed:", error);
              // Don't show error notification for auto-validation failures
            }
          }, 100);
        } catch (error) {
          console.error("Failed to load pipeline:", error);
          addNotification("Failed to load pipeline", "error");
          navigate("/");
        } finally {
          setLoading(false);
        }
      } else {
        // Create mode - initialize with system input node
        reset();
        setNodes([SYSTEM_INPUT_NODE]);
        setEdges([]);
        saveOriginalState();
        markClean(); // Mark as clean after initialization
      }
    };

    initializePipeline();
  }, [
    id,
    loadPipeline,
    reset,
    setNodes,
    setEdges,
    saveOriginalState,
    setLoading,
    addNotification,
    navigate,
    markClean,
    setValidationResults,
    theme,
  ]);

  // Update edge colors when theme changes
  const updateEdgeColorsForTheme = usePipelineBuilderStore(
    (state) => state.updateEdgeColorsForTheme,
  );
  useEffect(() => {
    updateEdgeColorsForTheme(theme);
  }, [theme, updateEdgeColorsForTheme]);

  // Note: Save/Cancel handlers are now in PipelineToolbar component

  // React Flow instance and refs for drop handling
  const reactFlowInstance = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [, setDropTargetInitialized] = useState(false);

  // State for execution edge removal confirmation modal
  const [showRemoveExecutionModal, setShowRemoveExecutionModal] =
    useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<{
    executionEdgeId: string;
    targetNodeId: string;
    targetNodeName: string;
    affectedDataEdgeIds: string[];
  } | null>(null);

  // State for sub-agent creation dialog (SWRM-121)
  const [showSubAgentDialog, setShowSubAgentDialog] = useState(false);
  const [pendingSubAgentConnection, setPendingSubAgentConnection] = useState<{
    orchestratorId: string;
    firstNodeId: string;
    processorName: string;
    connection: Connection;
  } | null>(null);

  // State for custom input creation dialog
  const [showInputDialog, setShowInputDialog] = useState(false);
  const [inputDialogNodeId, setInputDialogNodeId] = useState<string | null>(
    null,
  );

  // State for Pipeline Assistant
  const [showAssistant, setShowAssistant] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Handle opening AI assistant - create draft if no pipeline ID
  const handleOpenAssistant = async () => {
    // If we already have an ID, just open the assistant
    if (id) {
      setShowAssistant(!showAssistant);
      return;
    }

    // No ID yet - need to create a draft pipeline first
    try {
      setSavingDraft(true);

      const steps = buildPipelineSteps(nodes, edges);

      const response = await apiService.createPipeline({
        name: pipelineName || "Untitled Pipeline",
        description: pipelineDescription || undefined,
        version: pipelineVersion,
        status: "DRAFT", // Always create as draft for unsaved pipelines
        steps: steps as any,
      });

      addNotification("Draft pipeline created", "success");

      // Navigate to the new pipeline URL (this will reload with the ID)
      navigate(`/pipelines/builder/${response.id}`);

      // The assistant will open automatically on the next render when id exists
      // We use a setTimeout to ensure the navigation completes first
      setTimeout(() => {
        setShowAssistant(true);
      }, 100);
    } catch (error: any) {
      addNotification(
        `Failed to create draft pipeline: ${formatApiError(error)}`,
        "error",
      );
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle drop on canvas
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      try {
        const data = JSON.parse(
          e.dataTransfer.getData("application/reactflow"),
        );

        if (data.type === "processor") {
          // Position new node to the right of the rightmost node
          const NODE_WIDTH = 280; // Width of processor nodes in pixels
          const GAP = 55; // Desired gap between nodes (half of original 110px)
          let position;

          const currentNodes = usePipelineBuilderStore.getState().nodes;
          console.log(
            "🔍 DROP: Current nodes:",
            currentNodes.map((n) => ({ id: n.id, x: n.position.x })),
          );

          if (currentNodes.length > 0) {
            // Find the rightmost node
            const rightmostNode = currentNodes.reduce((max, node) =>
              node.position.x > max.position.x ? node : max,
            );

            console.log(
              "🔍 DROP: Rightmost node:",
              rightmostNode.id,
              "at x:",
              rightmostNode.position.x,
            );
            console.log(
              "🔍 DROP: Calculation:",
              rightmostNode.position.x,
              "+",
              NODE_WIDTH,
              "+",
              GAP,
              "=",
              rightmostNode.position.x + NODE_WIDTH + GAP,
            );

            // Position new node: rightmost left edge + node width + gap
            position = {
              x: rightmostNode.position.x + NODE_WIDTH + GAP,
              y: rightmostNode.position.y, // Same y position as rightmost node
            };

            console.log("🔍 DROP: New position:", position);
          } else {
            // If no nodes exist, use default position
            position = reactFlowInstance.project({
              x: e.clientX - reactFlowBounds.left,
              y: e.clientY - reactFlowBounds.top,
            });
          }

          // Generate unique step_id using timestamp
          const timestamp = Date.now();

          // Determine node type based on processor_type
          const processorType = (
            data.processor.processor_type || "SCRIPTED"
          ).toUpperCase();
          const nodeType =
            processorType === "ORCHESTRATOR" ? "orchestrator" : "processor";

          const defaultConfig = data.processor.default_config || {};
          const customInputs = defaultConfig.custom_inputs || [];

          // Generate unique prompt_anchor for LLM-based processors
          // Backend will lazily add this when AIA first reads the pipeline
          const processorName = data.processor.processor_name;
          const promptAnchor = `${processorName}_${timestamp.toString(36)}${Math.random().toString(36).substr(2, 4)}`;

          const newNode = {
            id: `${data.processor.processor_name}_${timestamp}`,
            type: nodeType,
            position,
            data: {
              step_id: `${data.processor.processor_name}_${timestamp}`,
              name: data.processor.processor_name,
              processor_name: data.processor.processor_name,
              processor_type: data.processor.processor_type,
              required_input_keys: data.processor.required_input_keys || [],
              optional_input_keys: data.processor.optional_input_keys || [],
              output_keys: data.processor.output_keys || [],
              output_types: data.processor.output_types || {}, // Include output types for type badges
              config: defaultConfig,
              custom_inputs: customInputs, // Extract custom_inputs to data level for UI
              prompt_anchor: promptAnchor,
              depends_on: [],
              isLocked: false,
              validation_errors: [],
            },
            draggable: true,
            deletable: true,
            selectable: true,
          };

          usePipelineBuilderStore.getState().addNode(newNode);
          addNotification(
            `Added ${data.processor.processor_name} to pipeline`,
            "success",
          );
        }
      } catch (error) {
        console.error("Failed to parse drop data:", error);
        addNotification("Failed to add processor", "error");
      }
    },
    [reactFlowInstance, addNotification],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  // Initialize drop target
  useEffect(() => {
    setDropTargetInitialized(true);
  }, []);

  // Node selection handlers
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: any) => {
      selectNode(node.id);
    },
    [selectNode],
  );

  const handlePaneClick = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  // Keyboard handler for Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        selectNode(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectNode]);

  // Custom onConnect handler to detect sub-agent connections (SWRM-121)
  const handleConnect = useCallback(
    (connection: Connection) => {
      // Check if this is a sub-agent connection (to orchestrator's sub_agent_output handle)
      if (
        connection.sourceHandle === "sub_agent_output" &&
        connection.source &&
        connection.target
      ) {
        // Find the orchestrator node
        const orchestratorNode = nodes.find((n) => n.id === connection.source);
        if (!orchestratorNode || orchestratorNode.type !== "orchestrator") {
          console.error("Source is not an orchestrator node");
          return;
        }

        // Find the target node
        const targetNode = nodes.find((n) => n.id === connection.target);
        if (!targetNode) {
          console.error("Target node not found");
          return;
        }

        // Check if target already belongs to a sub-agent
        if (targetNode.data.orchestratorNodeId) {
          addNotification("Node already belongs to a sub-agent", "error");
          return;
        }

        // Show sub-agent creation dialog
        setPendingSubAgentConnection({
          orchestratorId: connection.source,
          firstNodeId: connection.target,
          processorName: targetNode.data.processor_name,
          connection,
        });
        setShowSubAgentDialog(true);

        // Don't call the store's onConnect yet - wait for user to name the sub-agent
        return;
      }

      // For all other connections, use the store's onConnect handler
      onConnect(connection);
    },
    [nodes, onConnect, addNotification],
  );

  // Handle sub-agent creation dialog confirmation
  const handleSubAgentConfirm = useCallback(
    (config: { name: string; display_name: string; description: string }) => {
      if (!pendingSubAgentConnection) return;

      // IMPORTANT: Create the edge FIRST so that membership propagation can follow the connection
      onConnect(pendingSubAgentConnection.connection);

      // Then create the sub-agent and assign the first node (will propagate via edge)
      addSubAgent(
        pendingSubAgentConnection.orchestratorId,
        pendingSubAgentConnection.firstNodeId,
        config,
      );

      // Close dialog and clear pending state
      setShowSubAgentDialog(false);
      setPendingSubAgentConnection(null);

      addNotification(`Created sub-agent: ${config.display_name}`, "success");
    },
    [pendingSubAgentConnection, addSubAgent, onConnect, addNotification],
  );

  // Handle sub-agent creation dialog cancel
  const handleSubAgentCancel = useCallback(() => {
    setShowSubAgentDialog(false);
    setPendingSubAgentConnection(null);
  }, []);

  // Watch for pending input connections (when user drags to "add-input" handle)
  useEffect(() => {
    if (pendingInputConnection) {
      setInputDialogNodeId(pendingInputConnection.nodeId);
      setShowInputDialog(true);
    }
  }, [pendingInputConnection]);

  // Handle custom input creation dialog confirmation
  const handleInputDialogConfirm = useCallback(
    (name: string, description?: string) => {
      if (inputDialogNodeId && pendingInputConnection) {
        // 1. Create the custom input
        addCustomInput(inputDialogNodeId, name, description);

        // 2. The edge might already exist (created by React Flow during drag)
        // So instead of creating a new edge, we need to update the node's input_artifact_keys
        // to include the type information from the source output
        const connection = {
          ...pendingInputConnection.connection,
          targetHandle: `input-${name}`,
        };

        setTimeout(() => {
          // Get source node to extract output type
          const { nodes } = usePipelineBuilderStore.getState();
          const sourceNode = nodes.find((n) => n.id === connection.source);
          const sourceOutputKey = connection.sourceHandle?.replace(
            "output-",
            "",
          );
          const sourceOutputType =
            sourceNode?.data.output_types?.[sourceOutputKey || ""] || "string";

          console.log("[InputDialog] Updating input metadata:", {
            inputName: name,
            sourceOutputKey,
            sourceOutputType,
            sourceNodeOutputTypes: sourceNode?.data.output_types,
          });

          // Update the target node's input_artifact_keys with full metadata including type
          const updatedNodes = nodes.map((node) => {
            if (node.id === inputDialogNodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  input_artifact_keys: {
                    ...node.data.input_artifact_keys,
                    [name]: {
                      source: "artifact" as const,
                      from_step: connection.source || "",
                      key: sourceOutputKey || "",
                      type: sourceOutputType,
                    },
                  },
                },
              };
            }
            return node;
          });

          usePipelineBuilderStore.setState({
            nodes: updatedNodes,
            isDirty: true,
          });

          // Also try to create the edge in case it doesn't exist yet
          onConnect(connection);
        }, 100);
      }
      setShowInputDialog(false);
      setInputDialogNodeId(null);
      setPendingInputConnection(null);
    },
    [
      inputDialogNodeId,
      pendingInputConnection,
      addCustomInput,
      setPendingInputConnection,
      onConnect,
    ],
  );

  // Handle custom input creation dialog cancel
  const handleInputDialogCancel = useCallback(() => {
    setShowInputDialog(false);
    setInputDialogNodeId(null);
    setPendingInputConnection(null);
  }, [setPendingInputConnection]);

  // Handle edge deletion with confirmation for execution edges
  const handleEdgesDelete = useCallback(
    (deletedEdges: any[]) => {
      // Check if any of the deleted edges are execution flow edges
      const executionEdge = deletedEdges.find(
        (e) => e.sourceHandle === "next_node",
      );

      if (executionEdge) {
        // Find data edges that would be orphaned
        const affectedDataEdges = findDependentDataEdges(
          executionEdge.target,
          edges,
        );

        if (affectedDataEdges.length > 0) {
          // Show confirmation modal (per Q4)
          const targetNode = nodes.find((n) => n.id === executionEdge.target);
          setPendingRemoval({
            executionEdgeId: executionEdge.id,
            targetNodeId: executionEdge.target,
            targetNodeName: targetNode?.data.name || executionEdge.target,
            affectedDataEdgeIds: affectedDataEdges.map((e) => e.id),
          });
          setShowRemoveExecutionModal(true);
          return; // Don't delete yet - wait for confirmation
        }
      }

      // Normal deletion (no confirmation needed)
      const edgeIdsToDelete = deletedEdges.map((e) => e.id);
      const updatedEdges = edges.filter((e) => !edgeIdsToDelete.includes(e.id));
      setEdges(updatedEdges);
    },
    [edges, nodes, setEdges],
  );

  // Confirm execution edge removal
  const handleConfirmRemoveExecution = useCallback(() => {
    if (pendingRemoval) {
      // Remove both execution edge and affected data edges
      const edgeIdsToRemove = [
        pendingRemoval.executionEdgeId,
        ...pendingRemoval.affectedDataEdgeIds,
      ];
      const updatedEdges = edges.filter((e) => !edgeIdsToRemove.includes(e.id));
      setEdges(updatedEdges);
    }

    setShowRemoveExecutionModal(false);
    setPendingRemoval(null);
  }, [pendingRemoval, edges, setEdges]);

  // Cancel execution edge removal
  const handleCancelRemoveExecution = useCallback(() => {
    setShowRemoveExecutionModal(false);
    setPendingRemoval(null);
  }, []);

  // Automatically update order badges when execution flow changes
  useEffect(() => {
    // Get current nodes from store to avoid stale closure
    const currentNodes = usePipelineBuilderStore.getState().nodes;
    const orderMap = calculateExecutionOrder(currentNodes, edges);

    // Update nodes with new order values
    const updatedNodes = currentNodes.map((node) => {
      const newOrder = orderMap.get(node.id);

      // Only update if order changed (to avoid unnecessary re-renders)
      if (node.data.order !== newOrder) {
        return {
          ...node,
          data: {
            ...node.data,
            order: newOrder,
          },
        };
      }

      return node;
    });

    // Check if any nodes actually changed
    const hasChanges = updatedNodes.some(
      (node, index) => node.data.order !== currentNodes[index].data.order,
    );

    // Only update if something changed
    if (hasChanges) {
      setNodes(updatedNodes);
    }
  }, [edges, setNodes]); // Only edges triggers recalculation, setNodes is stable

  // Dark mode styling for React Flow
  const reactFlowClassName = theme === "dark" ? "dark" : "";

  // Connection line style for dragging preview
  const connectionLineStyle = {
    stroke: theme === "dark" ? "#e5e5e5" : "#64748b", // Slate-500 for light mode visibility
    strokeWidth: 4,
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Loading pipeline...
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Pipeline Toolbar */}
      <PipelineToolbar />

      {/* Main content area with palette and canvas */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Processor Palette */}
        <ProcessorPalette />

        {/* React Flow Canvas */}
        <div
          ref={reactFlowWrapper}
          className="flex-1"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={handleConnect}
            onEdgesDelete={handleEdgesDelete}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionLineStyle={connectionLineStyle}
            connectionLineComponent={CustomConnectionLine}
            className={reactFlowClassName}
            fitView
            fitViewOptions={{
              maxZoom: 1.0, // Don't zoom larger than 100%
            }}
            minZoom={0.1}
            maxZoom={2}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color={theme === "dark" ? "#374151" : "#e5e7eb"}
            />
            <Controls className={theme === "dark" ? "dark" : ""} />
            <MiniMap
              nodeColor={theme === "dark" ? "#4b5563" : "#d1d5db"}
              maskColor={
                theme === "dark" ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.1)"
              }
              className={theme === "dark" ? "dark" : ""}
            />

            {/* Info panel */}
            <Panel
              position="top-right"
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 m-4"
            >
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <div className="font-medium mb-1">Pipeline Builder</div>
                <div>Nodes: {nodes.length}</div>
                <div>Connections: {edges.length}</div>
              </div>

              {/* AI Assistant Toggle */}
              <button
                onClick={handleOpenAssistant}
                disabled={savingDraft}
                className={`mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                  showAssistant
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
                title={
                  savingDraft
                    ? "Creating draft..."
                    : showAssistant
                    ? "Close AI Assistant"
                    : id
                    ? "Open AI Assistant"
                    : "Open AI Assistant (will save draft)"
                }
              >
                <Bot className="h-4 w-4" />
                <span>{savingDraft ? "Saving..." : "AI Assistant"}</span>
              </button>
            </Panel>
          </ReactFlow>
        </div>
      </div>

      {/* Properties Panel */}
      <PropertiesPanel />

      {/* Remove Execution Edge Confirmation Modal */}
      <RemoveExecutionConfirmModal
        isOpen={showRemoveExecutionModal}
        nodeName={pendingRemoval?.targetNodeName || ""}
        dataEdgeCount={pendingRemoval?.affectedDataEdgeIds.length || 0}
        onConfirm={handleConfirmRemoveExecution}
        onCancel={handleCancelRemoveExecution}
      />

      {/* Sub-Agent Creation Dialog (SWRM-121) */}
      <SubAgentCreationDialog
        isOpen={showSubAgentDialog}
        onClose={handleSubAgentCancel}
        onConfirm={handleSubAgentConfirm}
        existingNames={
          pendingSubAgentConnection
            ? (
                nodes.find(
                  (n) => n.id === pendingSubAgentConnection.orchestratorId,
                )?.data.config?.sub_agents || []
              ).map((sa: any) => sa.name)
            : []
        }
        processorName={pendingSubAgentConnection?.processorName || ""}
      />

      {/* Custom Input Creation Dialog */}
      <InputNameDialog
        isOpen={showInputDialog}
        existingInputNames={
          inputDialogNodeId
            ? nodes
                .find((n) => n.id === inputDialogNodeId)
                ?.data.custom_inputs?.map(
                  (ci: { name: string; description?: string }) => ci.name,
                ) || []
            : []
        }
        onConfirm={handleInputDialogConfirm}
        onCancel={handleInputDialogCancel}
      />

      {/* AI Assistant Sidebar */}
      {showAssistant && id && (
        <AssistantSidebar
          pipelineId={parseInt(id, 10)}
          onClose={() => setShowAssistant(false)}
        />
      )}
    </div>
  );
};

// Wrapper component that provides ReactFlow context
export const PipelineBuilder: React.FC = () => {
  return (
    <ReactFlowProvider>
      <PipelineBuilderInner />
    </ReactFlowProvider>
  );
};
